import { useEffect, useState } from "react";
import { useBilling } from "../contexts/BillingContext";
import { Button } from "../components/ui/button";
import { Check, Crown, Sparkles, X, Gift } from "lucide-react";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import RedeemInviteCode from "../components/RedeemInviteCode";

const FEATURES = [
  { label: "Active tasks", free: "50", pro: "Unlimited", proOnly: false },
  { label: "Family members", free: "1 invitee", pro: "Unlimited", proOnly: false },
  { label: "Custom dashboard themes", free: "—", pro: "Accent picker", proOnly: true },
  { label: "Advanced analytics (CSV export, YoY trends)", free: "—", pro: "Included", proOnly: true },
  { label: "Priority email support", free: "—", pro: "Included", proOnly: true },
  { label: "Cross-device real-time sync", free: "Included", pro: "Included", proOnly: false },
  { label: "Kanban + Calendar + Notifications", free: "Included", pro: "Included", proOnly: false },
];

const PLANS = [
  { key: "monthly", label: "Monthly", durationLabel: "/mo", monthsCovered: 1 },
  { key: "semi_annual", label: "6 months", durationLabel: "/6 mo", monthsCovered: 6 },
  { key: "annual", label: "Annual", durationLabel: "/yr", monthsCovered: 12 },
];

export default function Pricing() {
  const { status, startTrial, checkout, refresh } = useBilling();
  const [plan, setPlan] = useState("monthly");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isPro = !!status?.is_pro;
  const hasUsedTrial = !!status?.has_used_trial;
  const isTrialing = status?.subscription_status === "trialing";
  const trialDays = status?.trial_days || 14;
  const trialRemaining = status?.trial_remaining_days || 0;

  const monthly = status?.prices?.monthly?.amount_dollars ?? 4.99;
  const semi = status?.prices?.semi_annual?.amount_dollars ?? 25;
  const annual = status?.prices?.annual?.amount_dollars ?? 49;

  const planConfig = PLANS.find((p) => p.key === plan);
  const planAmount = plan === "monthly" ? monthly : plan === "semi_annual" ? semi : annual;
  const monthsCovered = planConfig.monthsCovered;
  const effectiveMonthly = planAmount / monthsCovered;
  const savings = monthsCovered === 1
    ? 0
    : Math.round(((monthly * monthsCovered - planAmount) / (monthly * monthsCovered)) * 100);

  const handleTrial = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await startTrial();
      toast.success(`${trialDays}-day free trial started — enjoy Pro!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trial");
    } finally {
      setBusy(false);
    }
  };

  const handleCheckout = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await checkout(plan);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="px-4 sm:px-8 py-10 max-w-5xl mx-auto">
      <div className="text-center mb-10 anim-fade-up">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-primary bg-primary/10 border border-primary/20 rounded-full px-3 py-1 mb-5">
          <Sparkles size={11} /> Upgrade
        </div>
        <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tight leading-none mb-3">
          Get more done with <span className="text-primary">Pro</span>.
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
          Unlimited tasks, unlimited family members, custom themes, advanced analytics
          and priority support. Cancel anytime.
        </p>

        {isPro && (
          <div data-testid="pricing-pro-badge" className="inline-flex items-center gap-2 mt-6 text-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-500 rounded-full px-4 py-1.5">
            <Crown size={14} /> You're on Pro
            {isTrialing && ` · ${trialRemaining} trial day${trialRemaining === 1 ? "" : "s"} left`}
          </div>
        )}
      </div>

      {/* 3-way billing-cycle segmented control */}
      <div
        data-testid="pricing-cycle-switch"
        className="mx-auto inline-flex p-1 rounded-full border border-border bg-secondary/40 mb-2 max-w-full overflow-x-auto"
        role="tablist"
      >
        {PLANS.map((p) => {
          const active = plan === p.key;
          const localMonthly = p.key === "monthly" ? monthly : p.key === "semi_annual" ? semi / 6 : annual / 12;
          const localSavings = p.monthsCovered === 1
            ? 0
            : Math.round(((monthly * p.monthsCovered - (p.key === "semi_annual" ? semi : annual)) / (monthly * p.monthsCovered)) * 100);
          return (
            <button
              key={p.key}
              data-testid={`pricing-cycle-${p.key}`}
              onClick={() => setPlan(p.key)}
              role="tab"
              aria-selected={active}
              className={cn(
                "relative px-4 sm:px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap",
                active
                  ? "bg-primary text-primary-foreground shadow"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <span>{p.label}</span>
              {localSavings > 0 && (
                <span
                  className={cn(
                    "ml-2 text-[10px] px-1.5 py-0.5 rounded font-bold align-middle",
                    active
                      ? "bg-primary-foreground/20 text-primary-foreground"
                      : "bg-primary/10 text-primary border border-primary/20"
                  )}
                >
                  Save {localSavings}%
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p className="text-center text-xs text-muted-foreground mb-7">
        Compare to ${monthly.toFixed(2)}/mo if billed monthly.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Free */}
        <div data-testid="pricing-card-free" className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-xl font-bold">Free</h2>
          <p className="text-sm text-muted-foreground mt-1">Personal productivity, organised.</p>
          <div className="mt-5 mb-6">
            <span className="font-display text-5xl font-black tracking-tight">$0</span>
            <span className="text-sm text-muted-foreground ml-1">/forever</span>
          </div>
          <Button variant="outline" className="w-full" disabled data-testid="pricing-free-btn">
            Current plan
          </Button>
          <ul className="mt-6 space-y-2.5 text-sm">
            {FEATURES.map((f) => (
              <li key={f.label} className="flex items-start gap-2.5">
                {f.proOnly ? (
                  <X size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                ) : (
                  <Check size={15} className="text-emerald-500 mt-0.5 shrink-0" />
                )}
                <span className="flex-1">
                  {f.label}
                  <span className="block text-xs text-muted-foreground">{f.free}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Pro */}
        <div
          data-testid="pricing-card-pro"
          className="relative rounded-2xl border-2 border-primary bg-card p-6 overflow-hidden"
        >
          <div className="absolute inset-x-0 top-0 h-1.5 bg-primary" />
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold flex items-center gap-2">
              <Crown size={18} className="text-primary" /> Pro
            </h2>
            <span className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold">Most popular</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">Everything in Free, plus unlimited everything.</p>
          <div className="mt-5 mb-6">
            <span className="font-display text-5xl font-black tracking-tight">
              ${planAmount.toFixed(2)}
            </span>
            <span className="text-sm text-muted-foreground ml-1">{planConfig.durationLabel}</span>
            {monthsCovered > 1 && (
              <div className="text-xs text-muted-foreground mt-1">
                Just ${effectiveMonthly.toFixed(2)}/month, billed every {monthsCovered} months
                {savings > 0 && ` · Save ${savings}%`}
              </div>
            )}
          </div>

          {isPro ? (
            <Button variant="outline" className="w-full" disabled data-testid="pricing-current-pro-btn">
              <Crown size={14} className="mr-1.5" /> Active
            </Button>
          ) : (
            <div className="space-y-2">
              {!hasUsedTrial && (
                <Button
                  onClick={handleTrial}
                  disabled={busy}
                  className="w-full h-11"
                  data-testid="pricing-trial-btn"
                >
                  Start {trialDays}-day free trial
                </Button>
              )}
              <Button
                onClick={handleCheckout}
                disabled={busy}
                variant={hasUsedTrial ? "default" : "outline"}
                className="w-full h-11"
                data-testid="pricing-upgrade-btn"
              >
                {busy
                  ? "Redirecting…"
                  : `Upgrade — $${planAmount.toFixed(2)}${planConfig.durationLabel}`}
              </Button>
            </div>
          )}
          <p className="text-[10px] text-center text-muted-foreground mt-3">
            No card needed for trial. Cancel anytime.
          </p>

          <ul className="mt-6 space-y-2.5 text-sm">
            {FEATURES.map((f) => (
              <li key={f.label} className="flex items-start gap-2.5">
                <Check size={15} className="text-primary mt-0.5 shrink-0" />
                <span className="flex-1">
                  {f.label}
                  <span className="block text-xs text-muted-foreground">{f.pro}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="text-center text-xs text-muted-foreground mt-10">
        Secure payments by Stripe
      </p>

      {/* Redeem an invite code — collapsible, secondary, not pushy */}
      <div
        data-testid="pricing-redeem-block"
        className="mt-10 max-w-md mx-auto"
      >
        <details className="rounded-xl border border-border bg-card group">
          <summary
            data-testid="pricing-redeem-summary"
            className="flex items-center gap-2 px-4 py-3 cursor-pointer text-sm font-medium select-none list-none"
          >
            <Gift size={14} className="text-amber-500" />
            <span>Have an invite code?</span>
            <span className="ml-auto text-xs text-muted-foreground group-open:hidden">
              Click to expand
            </span>
          </summary>
          <div className="px-4 pb-4 pt-1">
            <RedeemInviteCode compact />
          </div>
        </details>
      </div>
    </div>
  );
}
